/**
 * Project Switcher Component
 *
 * Provides UI for switching between projects and managing project roots.
 * Supports keyboard shortcuts and recent project access.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuShortcut,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  FolderOpen,
  FolderPlus,
  ChevronDown,
  Clock,
  HardDrive,
  Cloud,
  Trash2,
  Check,
  AlertCircle,
} from 'lucide-react';
import {
  getProjectRootManager,
  type ProjectRoot,
} from '@/core/services/project-root';
import { formatBytes } from '@/lib/utils';
import { toast } from 'sonner';

interface ProjectSwitcherProps {
  className?: string;
}

export const ProjectSwitcher: React.FC<ProjectSwitcherProps> = ({
  className,
}) => {
  const [projects, setProjects] = useState<ProjectRoot[]>([]);
  const [currentProject, setCurrentProject] = useState<ProjectRoot | null>(
    null
  );
  const [recentProjects, setRecentProjects] = useState<ProjectRoot[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectType, setNewProjectType] = useState<
    'filesystem' | 'virtual'
  >('filesystem');
  const [virtualProjectName, setVirtualProjectName] = useState('');

  const manager = getProjectRootManager();

  // Load projects on mount
  useEffect(() => {
    const loadProjects = () => {
      setProjects(manager.getAllProjects());
      setCurrentProject(manager.getCurrentProject());
      setRecentProjects(manager.getRecentProjects());
    };

    loadProjects();

    // Listen for project events
    const handleProjectAdded = () => loadProjects();
    const handleProjectRemoved = () => loadProjects();
    const handleProjectSwitched = () => {
      setCurrentProject(manager.getCurrentProject());
      setRecentProjects(manager.getRecentProjects());
    };

    manager.on('project-added', handleProjectAdded);
    manager.on('project-removed', handleProjectRemoved);
    manager.on('project-switched', handleProjectSwitched);

    return () => {
      manager.off('project-added', handleProjectAdded);
      manager.off('project-removed', handleProjectRemoved);
      manager.off('project-switched', handleProjectSwitched);
    };
  }, []);

  // Keyboard shortcut for project switching
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSwitchProject = useCallback(async (projectId: string) => {
    const success = await manager.switchProject(projectId);
    if (success) {
      toast.success('Project switched successfully');
      setIsOpen(false);
      // Reload the page to ensure clean state
      window.location.reload();
    } else {
      toast.error('Failed to switch project');
    }
  }, []);

  const handleAddFilesystemProject = useCallback(async () => {
    const project = await manager.addProjectFromFilesystem();
    if (project) {
      toast.success(`Added project: ${project.name}`);
      setShowNewProjectDialog(false);
      await handleSwitchProject(project.id);
    }
  }, [handleSwitchProject]);

  const handleAddVirtualProject = useCallback(async () => {
    if (!virtualProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    const project = await manager.addVirtualProject(virtualProjectName);
    toast.success(`Created virtual project: ${project.name}`);
    setShowNewProjectDialog(false);
    setVirtualProjectName('');
    await handleSwitchProject(project.id);
  }, [virtualProjectName, handleSwitchProject]);

  const handleRemoveProject = useCallback(
    async (projectId: string, e: React.MouseEvent) => {
      e.stopPropagation();

      if (!confirm('Are you sure you want to remove this project?')) {
        return;
      }

      const success = await manager.removeProject(projectId);
      if (success) {
        toast.success('Project removed');
      } else {
        toast.error('Cannot remove current project');
      }
    },
    []
  );

  const getProjectIcon = (type: ProjectRoot['type']) => {
    switch (type) {
      case 'filesystem':
        return <HardDrive className="h-4 w-4" />;
      case 'virtual':
        return <Cloud className="h-4 w-4" />;
      case 'tauri':
        return <FolderOpen className="h-4 w-4" />;
    }
  };

  const formatLastAccessed = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={`min-w-[200px] justify-between ${className}`}
          >
            <div className="flex items-center gap-2">
              {currentProject && getProjectIcon(currentProject.type)}
              <span className="truncate">
                {currentProject?.name || 'No Project'}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[300px]">
          <DropdownMenuLabel>Projects</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {recentProjects.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Recent
              </DropdownMenuLabel>
              {recentProjects.slice(0, 5).map((project) => (
                <DropdownMenuItem
                  key={project.id}
                  onClick={() => handleSwitchProject(project.id)}
                  className="group"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      {getProjectIcon(project.type)}
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {project.name}
                          {project.id === currentProject?.id && (
                            <Check className="inline-block h-3 w-3 ml-1" />
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatLastAccessed(project.lastAccessed)}
                        </span>
                      </div>
                    </div>
                    {project.id !== currentProject?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => handleRemoveProject(project.id, e)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}

          {projects.length > recentProjects.length && (
            <>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                All Projects
              </DropdownMenuLabel>
              {projects
                .filter((p) => !recentProjects.find((r) => r.id === p.id))
                .map((project) => (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => handleSwitchProject(project.id)}
                    className="group"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        {getProjectIcon(project.type)}
                        <span className="text-sm">{project.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => handleRemoveProject(project.id, e)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </DropdownMenuItem>
                ))}
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem onClick={() => setShowNewProjectDialog(true)}>
            <FolderPlus className="mr-2 h-4 w-4" />
            Add New Project Root
            <DropdownMenuShortcut>⌘N</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Current Project Badge */}
      {currentProject && (
        <Badge variant="secondary" className="ml-2">
          <Clock className="h-3 w-3 mr-1" />
          {currentProject.metadata.fileCount} files
          {currentProject.metadata.totalSize > 0 && (
            <span className="ml-1">
              • {formatBytes(currentProject.metadata.totalSize)}
            </span>
          )}
        </Badge>
      )}

      {/* New Project Dialog */}
      <Dialog
        open={showNewProjectDialog}
        onOpenChange={setShowNewProjectDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Project Root</DialogTitle>
            <DialogDescription>
              Choose how you want to create a new project root directory.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant={
                  newProjectType === 'filesystem' ? 'default' : 'outline'
                }
                className="h-auto flex-col py-4"
                onClick={() => setNewProjectType('filesystem')}
              >
                <HardDrive className="h-8 w-8 mb-2" />
                <div className="text-sm font-medium">File System</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Select a folder on your computer
                </div>
              </Button>

              <Button
                variant={newProjectType === 'virtual' ? 'default' : 'outline'}
                className="h-auto flex-col py-4"
                onClick={() => setNewProjectType('virtual')}
              >
                <Cloud className="h-8 w-8 mb-2" />
                <div className="text-sm font-medium">Virtual</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Browser-only storage
                </div>
              </Button>
            </div>

            {newProjectType === 'filesystem' && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground">
                    You'll be prompted to select a folder. The browser will ask
                    for permission to read and write files in that folder.
                  </div>
                </div>
              </div>
            )}

            {newProjectType === 'virtual' && (
              <div className="space-y-2">
                <label htmlFor="project-name" className="text-sm font-medium">
                  Project Name
                </label>
                <input
                  id="project-name"
                  type="text"
                  value={virtualProjectName}
                  onChange={(e) => setVirtualProjectName(e.target.value)}
                  placeholder="My Project"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddVirtualProject();
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Virtual projects store data in your browser only.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewProjectDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={
                newProjectType === 'filesystem'
                  ? handleAddFilesystemProject
                  : handleAddVirtualProject
              }
            >
              {newProjectType === 'filesystem'
                ? 'Select Folder'
                : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
